import {
  CardContent,
  CircularProgress,
  Divider,
  Paper,
  Typography,
  createStyles,
  makeStyles,
} from '@material-ui/core';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import { navigate } from 'gatsby';
import React, { useContext } from 'react';
import {
  AccountParams,
  EnergyTrackerData,
} from '../../../../__generated__/pge-types';
import { useTranslation } from '../../../../hooks/useTranslation';
import routes from '../../../../routes';
import { useIsMobile } from '../../../../util/style-utils';
import ErrorOverlay from '../../../error/error-overlay';
import { FullDivider } from '../../../pge-plus-common/FullDivider';
import { useCurrentUseCard } from './useCurrentUseCard';
import mainColors from '../../../../themes/main-colors';
import {
  LanguageContext,
  SupportedLanguages,
} from '../../../../providers/LanguageProvider';

const useStyles = makeStyles(() =>
  createStyles({
    paper: {
      cursor: 'pointer',
    },
    centerChildren: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    },
    cardContent: {
      paddingBottom: '5px !important',
    },
    title: {
      fontFamily: 'Forma-DJR-Display',
      marginTop: '0',
      fontWeight: 100,
      fontSize: '20px',
      marginBottom: '0',
    },
    titleDivider: {
      width: '100%',
      backgroundColor: mainColors.lightGray1,
      height: '1px',
      border: 0,
      margin: '8px 0',
    },
    cardDetails: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
    subText: {
      margin: 0,
      marginBottom: '15px',
      fontSize: '16px',
    },
    dialContainer: {
      display: 'flex',
      width: '100%',
      background: mainColors.riverBlue,
      flexDirection: 'column',
      padding: '0 14px',
    },
    dial: {
      '&>svg': {
        height: '170px',
        width: '275px',
        paddingTop: '10px',
        marginTop: '20px',
      },
    },
    infoBox: {
      display: 'flex',
      flexDirection: 'row',
      width: '100%',
      background: mainColors.riverBlue,
      justifyContent: 'space-between',
      padding: '8px 16px',
    },
    customDivider: {
      width: '100%',
      backgroundColor: mainColors.white,
      height: '2px',
      border: 0,
    },
    nextBill: {
      textAlign: 'right',
    },
    cardFooter: {
      display: 'flex',
      justifyContent: 'space-between',
      width: '95%',
      cursor: 'pointer',
    },
    cardFooterText: {
      margin: 0,
      fontWeight: 'bold',
    },
    arrow: {
      marginLeft: '30px',
      marginTop: '-2px',
      alignContent: 'center',
    },
    footNote: {
      fontSize: '12px',
      marginTop: '8px',
      marginBottom: 0,
    },
  }),
);

type CurrentUseCardProps = {
  data:
    | {
        getEnergyTrackerData: EnergyTrackerData;
      }
    | undefined;
  loading: boolean;
  error: boolean;
  accountParams: AccountParams;
  isResAccount: boolean;
};
// Current use

export const CurrentUseCard = (props: CurrentUseCardProps) => {
  const {
    data: energyTrackerData,
    loading: energyTrackerLoading,
    error: energyTrackerError,
    accountParams,
    isResAccount,
  } = props;
  const classes = useStyles();
  const { inlineRichT, t } = useTranslation();
  const isMobile = useIsMobile();
  const { currentUseCardInfo, loading, error } = useCurrentUseCard(
    energyTrackerData,
    energyTrackerLoading,
    energyTrackerError,
    accountParams,
  );
  const handleClick = () => {
    void navigate(routes.MY_ENERGY_USE);
  };

  if (loading) {
    return (
      <Paper>
        <CardContent className={classes.centerChildren}>
          <CircularProgress data-testid="loader" />
        </CardContent>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper>
        <CardContent className={classes.centerChildren} data-testid="error">
          <ErrorOverlay title={t('CURRENT_USE')} hide />
        </CardContent>
      </Paper>
    );
  }

  const getSubtext = () => {
    if (currentUseCardInfo?.currentUseIndicator === 'same') {
      return isResAccount
        ? t('USAGE_ENERGY_SAME_AS_THIS_MONTH_MESSAGE')
        : t('BUSINESS_USAGE_ENERGY_SAME_AS_THIS_MONTH_MESSAGE');
    }

    if (currentUseCardInfo?.currentUseIndicator === 'less') {
      return isResAccount
        ? inlineRichT('YOU_ARE_CURRENTLY_USING_LESS_ENERGY_THAN_LAST_MONTH', {
            ENERYGY_USAGE_PERCENTAGE: currentUseCardInfo?.currentUsePercentage?.toString(),
          })
        : inlineRichT(
            'YOUR_BUSINESS_CURRENTLY_USING_LESS_ENERGY_THAN_LAST_MONTH',
            {
              ENERYGY_USAGE_PERCENTAGE: currentUseCardInfo?.currentUsePercentage?.toString(),
            },
          );
    }

    if (currentUseCardInfo?.currentUseIndicator === 'more') {
      return isResAccount
        ? inlineRichT('YOU_ARE_CURRENTLY_USING_MORE_ENERGY_THAN_LAST_MONTH', {
            ENERYGY_USAGE_PERCENTAGE: currentUseCardInfo?.currentUsePercentage?.toString(),
          })
        : inlineRichT(
            'YOUR_BUSINESS_CURRENTLY_USING_MORE_ENERGY_THAN_LAST_MONTH',
            {
              ENERYGY_USAGE_PERCENTAGE: currentUseCardInfo?.currentUsePercentage?.toString(),
            },
          );
    }
  };
  const { language } = useContext(LanguageContext);
  const estCurrentChargesText =
    language === SupportedLanguages.English
      ? `${t('EST_CURRENT')} ${t('CHARGES')}`
      : t('EST_CURRENT');

  return (
    <Paper className={classes.paper}>
      <CardContent className={classes.cardContent}>
        <div className={classes.cardDetails} onClick={() => handleClick()}>
          <h2 className={classes.title} data-testid="title">
            {t('CURRENT_USE')}
          </h2>
          <Divider className={classes.titleDivider} />
          <p className={classes.subText} data-testid="subtext">
            {getSubtext()}
          </p>
          <div
            className={classes.dialContainer}
            style={{
              marginBottom: currentUseCardInfo?.dialInfo.containerBottomMargin,
            }}
          >
            <div className={classes.dial} data-testid="dial">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox={`${currentUseCardInfo.dialInfo.p1} 0  ${currentUseCardInfo.dialInfo.x} ${currentUseCardInfo.dialInfo.y}`}
                style={{
                  height: currentUseCardInfo.dialInfo.height,
                }}
              >
                <circle
                  id="c1"
                  cx="50"
                  cy="50"
                  r="43"
                  fill="none"
                  stroke="#D7D7D7"
                  stroke-width="6"
                  stroke-dasharray="180 360"
                  pathLength="360"
                  stroke-linecap="round"
                  transform="rotate(180 50 50)"
                />
                <circle
                  id="c2"
                  cx="50"
                  cy="50"
                  r="43"
                  fill="none"
                  stroke="#3376BB"
                  stroke-width="6"
                  stroke-dasharray={`${currentUseCardInfo.dialInfo.angle} 360`}
                  pathLength="360"
                  stroke-linecap="round"
                  transform="rotate(180 50 50)"
                />
                <defs>
                  <linearGradient id="gradient" gradientTransform="rotate(90)">
                    <stop offset="5%" stop-color="#E4E4E4" />
                    <stop offset="45%" stop-color="#FFFFFF" />
                    <stop offset="100%" stop-color="white" />
                  </linearGradient>
                </defs>
                {/* <ellipse
                  cx="50"
                  cy="43"
                  rx="34"
                  ry="30"
                  fill="url(#gradient)"
                /> */}
                {/* Hide till the C2M issue is fixed */}
                {/* {false && currentUseCardInfo?.displayThreshold && (
                  <g
                    transform={`translate(50 50) rotate(${currentUseCardInfo.dialInfo.thresholdAngle})`}
                  >
                    <line
                      id="l1"
                      x1="-40"
                      y1="0"
                      x2="-50"
                      y2="0"
                      stroke="#84898F"
                      stroke-width=".3"
                    />
                    <text
                      id="t1"
                      x="63"
                      y="-1"
                      text-anchor="middle"
                      font-size="8"
                      font-weight="bold"
                      fill="#84898F"
                      font-family="Untitled-Sans"
                      writing-mode="vertical-lr"
                      transform="scale(-1, -1)"
                    >
                      {`$${currentUseCardInfo?.threshold}`}
                    </text>
                    <text
                      id="t2"
                      x="55"
                      y="-1"
                      text-anchor="middle"
                      font-size="7"
                      fill="#84898F"
                      font-family="Untitled-Sans"
                      writing-mode="vertical-lr"
                      transform="scale(-1, -1)"
                    >
                      {t('THRESHOLD')}
                    </text>
                  </g>
                )} */}
                <text
                  id="t3"
                  x="50"
                  y="40"
                  text-anchor="middle"
                  font-weight="bold"
                  fill="#20415B"
                  font-family="Untitled-Sans"
                >
                  {`$${currentUseCardInfo?.estimatedCurrentCharges}`}
                </text>
                <text
                  id="t4"
                  x="50"
                  y="50"
                  text-anchor="middle"
                  font-size="6"
                  fill="#20415B"
                  font-family="Untitled-Sans"
                >
                  {estCurrentChargesText}
                </text>
              </svg>
            </div>
            <Divider className={classes.customDivider} />
          </div>

          <div className={classes.infoBox}>
            <div>
              {inlineRichT('BILLING_CYCLE_CURRENT_DAY', {
                BILLING_CYCLE_CURRENT_DAY: currentUseCardInfo?.billingCycleCurrentDay?.toString(),
                BILLING_CYCLE_TOTAL_DAYS: currentUseCardInfo?.billingCycleTotalDays?.toString(),
              })}
            </div>
            <div
              className={classes.nextBill}
              style={{
                marginLeft: currentUseCardInfo?.dialInfo.marginLeft,
                marginBottom: currentUseCardInfo?.dialInfo.marginBottom,
              }}
              data-testid="nextbill"
            >
              {inlineRichT('EST_NEXT_BILL', {
                ESTIMATED_MIN_AMOUNT: currentUseCardInfo?.nextBillLowerBound?.toString(),
                ESTIMATED_MAX_AMOUNT: currentUseCardInfo?.nextBillHigherBound?.toString(),
              })}
            </div>
          </div>
          <p className={classes.footNote}>{t('CURRENT_USE_CARD_FOOT_NOTE')}</p>
          <FullDivider />
          <div className={classes.cardFooter} onClick={handleClick}>
            <p className={classes.cardFooterText} data-testid="footer">
              {t('VIEW_DETAILS')}
            </p>
            <div className={classes.arrow}>
              <ChevronRightIcon />
            </div>
          </div>
        </div>
      </CardContent>
    </Paper>
  );
};
